import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import styles from "./simulazioneRendimentiAnnoSolare.module.scss";
import { useTranslation } from "react-i18next";
import colorsStore from "shared/store/colorsStore";

const SimulazioneRendimentiAnnoSolare = ({ data }) => {
  const { t } = useTranslation();

  const formatValue = (value) => {
    if (value === null || value === undefined) return "0";
    return `${Number(value).toFixed(1)}`;
  };

  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const chartCopy = {
    fontSize: 11,
    fill: colorsConfig.finecoBlack,
    fontFamily: "Proxima Nova",
  };

  const renderCustomBarLabel = (props) => {
    const { x, y, width, value } = props;
    const formattedValue = formatValue(value);
    const isPositive = Number(value) >= 0;
    const yPos = isPositive ? y - 10 : y + 20;
    return (
      <text
        x={x + width / 2}
        y={yPos}
        fill={props.fill}
        textAnchor="middle"
        fontSize={12}
        fontFamily="Proxima Nova"
      >
        {`${formattedValue}%`}
      </text>
    );
  };

  const simulazioneRendimentiData =
    data.analisi_backtest.simulazione_rendimenti_portafoglio_anno_solare;
  const formattedData = simulazioneRendimentiData.map((item) => ({
    ...item,
    value: Math.round(item.value),
  }));

  if (data.length === 0) {
    return null;
  }

  // Custom tooltip component with TypeScript interface
  interface TooltipProps {
    active?: boolean;
    payload?: any[];
    label?: string;
  }

  const CustomTooltip: React.FC<TooltipProps> = ({
    active,
    payload,
    label,
  }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div
        style={{
          backgroundColor: colorsConfig.finecoWhite || "#fff",
          padding: "10px",
          border: `1px solid ${colorsConfig.FinecoGrayLight1 || "#ccc"}`,
          borderRadius: "4px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <p
          style={{
            marginBottom: "5px",
            fontWeight: "bold",
            fontFamily: "Proxima Nova",
            fontSize: "12px",
            color: colorsConfig.finecoBlack || "#000",
          }}
        >
          {`Anno: ${label}`}
        </p>
        {payload.map((entry, index) => (
          <p
            key={`tooltip-${index}`}
            style={{
              margin: "3px 0",
              fontFamily: "Proxima Nova",
              fontSize: "12px",
              color: entry.color,
            }}
          >
            {`${entry.name}: ${Number(entry.value).toFixed(1)}%`}
          </p>
        ))}
      </div>
    );
  };

  const values = formattedData.map((d) => d.value);
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);

  // Add clean 15% padding and round to nearest 5
  const paddedMin = Math.floor((rawMin - Math.abs(rawMin) * 0.5) / 5) * 5;
  const paddedMax = Math.ceil((rawMax + Math.abs(rawMax) * 0.5) / 5) * 5;

  // Generate ticks every 5 units
  const ticks = [];
  for (let i = paddedMin; i <= paddedMax; i += 5) {
    ticks.push(i);
  }

  return (
    <div className={styles.sectionContainer}>
      <div className={styles.sectionTitle}>
        {t("portfolio.simulazioneRendimentiAnnoSolare")}
      </div>
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={260} debounce={500}>
          <BarChart
            data={formattedData}
            margin={{ top: 20, right: 10, left: -10, bottom: 10 }}
          >
            <CartesianGrid
              strokeWidth={1}
              stroke={colorsConfig.GrayLight2}
              strokeOpacity={1}
              horizontal={true}
              vertical={false}
            />
            {/* Reference line at 0% */}
            <ReferenceLine
              y={0}
              stroke={colorsConfig.finecoBlack}
              strokeWidth={1}
              strokeOpacity={0.8}
            />
            <XAxis
              dataKey="date"
              tick={chartCopy}
              tickLine={false}
              tickFormatter={(value) =>
                new Date(value).toLocaleDateString("it-IT", { year: "numeric" })
              }
              dy={10}
              strokeDasharray="none"
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
            />
            <YAxis
              tickFormatter={formatValue}
              tickLine={false}
              tick={chartCopy}
              domain={[paddedMin, paddedMax]}
              ticks={ticks} // Force all ticks including those from the padding
              interval={0} // Force all ticks to render
              allowDataOverflow={false}
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
            />

            {/* <Legend
              content={<CustomizedLegend />}
              align="left"
              verticalAlign="bottom"
              height={36} // Increased height to accommodate the footnote
            /> */}
            <Bar
              dataKey="value"
              name={"Rendimenti"}
              fill={colorsConfig.FinecoBluePrimary}
              barSize={26}
              radius={[4, 4, 0, 0]}
              label={(props) =>
                renderCustomBarLabel({
                  ...props,
                  fill: colorsConfig.FinecoBlack,
                })
              }
              isAnimationActive={false}
            />
            {/* <Bar
              dataKey="category_return"
              name="Rendimento Categoria"
              fill={colorsConfig.CardCardGreen}
              barSize={26}
              radius={[4, 4, 0, 0]}
              label={(props) =>
                renderCustomBarLabel({
                  ...props,
                  fill: colorsConfig.FinecoBlack,
                })
              }
            /> */}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SimulazioneRendimentiAnnoSolare;
